import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, merge } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataPlatformService } from '../../data-platform/data-platform.service';
import {
  DataPlatformProfileSplitOptions,
  DataPlatformProfileSplitResponse,
} from '../../data-platform/data-platform.model';
import { DateService } from '../../shared/services/date-service';

export interface ProfileUnmergeDialogData {
  uid: number;
}

const FEATURE = 'profiles-flyout-unmerge';

const SIDES = {
  original: {
    titleKey: 'data-platform.unmerge-this-profile',
    icon: 'person',
    firstName: 'originalFirstName',
    lastName: 'originalLastName',
  },
  new: {
    titleKey: 'data-platform.unmerge-new-profile',
    icon: 'person_add',
    firstName: 'newFirstName',
    lastName: 'newLastName',
  },
} as const;

/** Closes with the uid of the profile to show next. */
@Component({
  selector: 'app-profile-unmerge-dialog',
  templateUrl: './profile-unmerge-dialog.component.html',
  styleUrls: ['./profile-unmerge-dialog.component.scss'],
  standalone: false,
})
export class ProfileUnmergeDialogComponent {
  private readonly data = inject<ProfileUnmergeDialogData>(MAT_DIALOG_DATA);
  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly dialogRef = inject<MatDialogRef<ProfileUnmergeDialogComponent, number>>(MatDialogRef);
  private readonly dateService = inject(DateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly options = signal<DataPlatformProfileSplitOptions | null>(null);
  protected readonly loadFailed = signal(false);
  protected readonly isSplitting = signal(false);
  /** Why the last unmerge failed, as a translation key. */
  protected readonly splitErrorKey = signal<string | null>(null);
  protected readonly result = signal<DataPlatformProfileSplitResponse | null>(null);
  private readonly movedIdentifiers = signal(new Set<string>());
  private readonly movedUids = signal(new Set<number>());

  protected readonly names = new FormGroup({
    originalFirstName: new FormControl('', { nonNullable: true }),
    originalLastName: new FormControl('', { nonNullable: true }),
    newFirstName: new FormControl('', { nonNullable: true }),
    newLastName: new FormControl('', { nonNullable: true }),
  });

  protected readonly identifierRows = computed(() =>
    (this.options()?.identifiers ?? []).map((identifier) => ({
      value: identifier.value,
      moved: this.movedIdentifiers().has(identifier.value),
    })),
  );

  protected readonly deviceRows = computed(() =>
    (this.options()?.devices ?? []).map((device) => ({
      uid: device.uid,
      browser: device.browser,
      os: device.os,
      usedWith: device.identifiers.join(', '),
      lastSeenDate: device.lastSeen ? this.dateService.utcToLocalTime(device.lastSeen) : null,
      moved: this.movedUids().has(device.uid),
    })),
  );

  /** Set when the profile has more devices than the dialog lists. */
  protected readonly unlistedDevices = computed(() => {
    const options = this.options();
    return options && options.deviceCount > options.devices.length
      ? { shown: options.devices.length, total: options.deviceCount }
      : null;
  });

  /** The rule the current choice breaks, as a translation key; null when it can be unmerged. */
  protected readonly problemKey = computed(() => {
    const identifiers = this.identifierRows();
    if (!identifiers.some((row) => row.moved)) {
      return 'data-platform.unmerge-choose-identifier';
    }
    if (identifiers.every((row) => row.moved)) {
      return 'data-platform.unmerge-keep-identifier';
    }
    if (!this.unlistedDevices() && this.deviceRows().every((row) => row.moved)) {
      return 'data-platform.unmerge-keep-device';
    }
    return null;
  });

  /** What each profile ends up with under the current choice. */
  protected readonly sides = computed(() => {
    const identifiers = this.identifierRows();
    const movedDevices = this.deviceRows().filter((row) => row.moved).length;
    return [
      side(
        'original',
        identifiers.filter((row) => !row.moved),
        (this.options()?.deviceCount ?? 0) - movedDevices,
        this.options()?.tags ?? [],
      ),
      side(
        'new',
        identifiers.filter((row) => row.moved),
        movedDevices,
        [],
      ),
    ];
  });

  protected readonly canUnmerge = computed(() => !this.problemKey() && !this.isSplitting());

  constructor() {
    // The dialog opens with disableClose, so a backdrop click or Escape goes through close() and its guard.
    merge(
      this.dialogRef.backdropClick(),
      this.dialogRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close());
    this.load();
  }

  private load(): void {
    this.options.set(null);
    this.loadFailed.set(false);
    this.movedIdentifiers.set(new Set());
    this.movedUids.set(new Set());
    this.dataPlatformService
      .getProfileSplitOptions(this.data.uid, FEATURE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.isPositive() || !result.data) {
          this.loadFailed.set(true);
          return;
        }
        this.options.set(result.data);
        this.names.patchValue({
          originalFirstName: result.data.firstName ?? '',
          originalLastName: result.data.lastName ?? '',
        });
      });
  }

  /** A device moves only while every identifier it was used with moves. */
  protected toggleIdentifier(value: string): void {
    const movedIdentifiers = withToggled(this.movedIdentifiers(), value);
    const movedUids = new Set(this.movedUids());
    for (const device of this.options()?.devices ?? []) {
      if (!device.identifiers.includes(value)) {
        continue;
      }
      if (device.identifiers.every((identifier) => movedIdentifiers.has(identifier))) {
        movedUids.add(device.uid);
      } else {
        movedUids.delete(device.uid);
      }
    }
    this.movedIdentifiers.set(movedIdentifiers);
    this.movedUids.set(movedUids);
  }

  protected toggleDevice(uid: number): void {
    this.movedUids.update((moved) => withToggled(moved, uid));
  }

  protected unmerge(): void {
    if (!this.canUnmerge()) {
      return;
    }
    this.isSplitting.set(true);
    this.splitErrorKey.set(null);
    this.dataPlatformService
      .splitProfile(
        this.data.uid,
        { identifiers: [...this.movedIdentifiers()], uids: [...this.movedUids()], ...this.names.getRawValue() },
        FEATURE,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isSplitting.set(false);
        if (result?.isPositive() && result.data) {
          this.result.set(result.data);
        } else if (result?.isBadRequest()) {
          // The profile changed since it loaded, e.g. by an unmerge whose response was lost.
          this.splitErrorKey.set('data-platform.unmerge-changed');
          this.load();
        } else {
          this.splitErrorKey.set('data-platform.unmerge-failed');
        }
      });
  }

  /** Ignored mid-unmerge: the server still finishes it and the flyout would miss the result. */
  protected close(openUid?: number): void {
    if (this.isSplitting()) {
      return;
    }
    this.dialogRef.close(openUid ?? this.result()?.originalUid);
  }
}

function side(key: keyof typeof SIDES, identifiers: { value: string }[], devices: number, tags: string[]) {
  return {
    key,
    ...SIDES[key],
    identifiers: identifiers.map((row) => row.value),
    tags,
    devices,
    devicesKey:
      devices === 0
        ? 'data-platform.unmerge-side-devices-none'
        : devices === 1
          ? 'data-platform.unmerge-side-devices-one'
          : 'data-platform.unmerge-side-devices',
  };
}

function withToggled<T>(values: Set<T>, value: T): Set<T> {
  const next = new Set(values);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}
